import { useCallback, useEffect, useRef, useState } from 'react';
import { socket } from '@/components/navegacao/socket';

/** De quanto em quanto tempo consultamos o /version.json publicado pelo build. */
const INTERVALO_VERIFICACAO_MS = 5 * 60 * 1000;

/** Piso entre duas consultas, para os gatilhos por evento não dispararem em rajada. */
const INTERVALO_MINIMO_MS = 60 * 1000;

/** Versão embutida no bundle que está rodando agora nesta aba. */
export const VERSAO_ATUAL = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : null;

async function buscarVersaoPublicada(): Promise<string | null> {
  try {
    const resp = await fetch(`/version.json?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    });

    // Em dev (e se o arquivo não existir) o fallback da SPA devolve o index.html com 200,
    // então o content-type é o que realmente diz se veio o version.json.
    if (!resp.ok || !resp.headers.get('content-type')?.includes('application/json'))
      return null;

    const data = await resp.json();
    return typeof data?.version === 'string' ? data.version : null;
  } catch {
    // Rede fora, deploy em andamento, etc. — tentamos de novo no próximo ciclo.
    return null;
  }
}

/**
 * Detecta que saiu uma versão nova do frontend comparando a versão embutida no
 * bundle carregado com a publicada em /version.json.
 */
export default function useAtualizacaoSistema() {
  const [versaoPublicada, setVersaoPublicada] = useState<string | null>(null);
  const ultimaVerificacao = useRef(0);

  const atualizacaoDisponivel =
    !!VERSAO_ATUAL && !!versaoPublicada && versaoPublicada !== VERSAO_ATUAL;

  useEffect(() => {
    // Sem versão embutida não há o que comparar; já detectado, não precisa continuar olhando.
    if (!VERSAO_ATUAL || atualizacaoDisponivel) return;

    let cancelado = false;

    const verificar = async () => {
      if (cancelado) return;
      if (Date.now() - ultimaVerificacao.current < INTERVALO_MINIMO_MS) return;
      ultimaVerificacao.current = Date.now();

      const versao = await buscarVersaoPublicada();
      if (!cancelado && versao) setVersaoPublicada(versao);
    };

    verificar();

    const timer = setInterval(verificar, INTERVALO_VERIFICACAO_MS);

    // Aba que ficou horas em segundo plano é justamente a que costuma estar velha.
    const onVisibilidade = () => {
      if (document.visibilityState === 'visible') verificar();
    };
    document.addEventListener('visibilitychange', onVisibilidade);

    // Deploy reinicia o backend: a reconexão do socket é um bom palpite de versão nova.
    socket.io.on('reconnect', verificar);

    return () => {
      cancelado = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilidade);
      socket.io.off('reconnect', verificar);
    };
  }, [atualizacaoDisponivel]);

  const recarregar = useCallback(() => {
    window.location.reload();
  }, []);

  return { atualizacaoDisponivel, versaoAtual: VERSAO_ATUAL, versaoPublicada, recarregar };
}
